// 등급별 가격 조회 및 관리 훅
import { useState, useCallback, useEffect } from 'react';
import { DeviceGrade } from '../../../../types/gradeSystem';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { firestorePriceApiService } from '../../../../services/firestoreGradePriceService';
import { canonicalizeModelId } from '../../../../utils/idCanonical';

// 🔒 SSOT 가드: 관리자 카탈로그(device_catalog)에 설정되지 않은 구성은 가격을 제공하지 않는다.
// [배경] 카탈로그에서 제거된 용량의 잔존 가격 문서(유령 데이터)가 unified_prices에 남아 있어도
//        사용자에게 노출되지 않도록 조회 단계에서 차단 (기타 용량 직접 입력 경로 포함)
const normalizeStorageKey = (s: string | undefined): string =>
  (s || '').toLowerCase().replace(/[^a-z0-9가-힣]/g, '');

const isStorageAllowedByCatalog = (model: any, storage: string): boolean => {
  const options: string[] | undefined = model?.storageOptions;
  // 카탈로그 메타데이터가 없는 모델은 차단하지 않음 (fail-open: 기존 동작 유지)
  if (!options || options.length === 0) return true;

  const key = normalizeStorageKey(storage);
  if (!options.some(opt => normalizeStorageKey(opt) === key)) return false;

  // 관리자 그리드와 동일한 disabledVariants 판정: 모든 연결 옵션에서 비활성이면 제외
  const disabledVariants: any[] = model?.disabledVariants || [];
  if (disabledVariants.length === 0) return true;
  const connectivities: string[] = model?.connectivityOptions?.length ? model.connectivityOptions : ['Wi-Fi'];
  const disabledForAll = connectivities.every(conn =>
    disabledVariants.some(v =>
      normalizeStorageKey(v?.storage) === key &&
      ((v?.connectivity || 'Wi-Fi') === (conn || 'Wi-Fi'))
    )
  );
  return !disabledForAll;
};

interface UseGradePricesParams {
  category: string;
  selectedManufacturer: 'APPLE' | 'SAMSUNG' | 'LG' | '';
  selectedIphoneModel: string;
  selectedIphoneStorage: string;
  customIphoneStorage: string;
  selectedSamsungModel: string;
  selectedSamsungStorage: string;
  customSamsungStorage: string;
  selectedSamsungTabletModel: string;
  selectedSamsungTabletStorage: string;
  customSamsungTabletStorage: string;
  selectedSamsungTabletConnectivity: string;
  selectedModel: any;
  selectedSubModel: any;
  firebaseAppleSmartphones: any[];
  firebaseSamsungSmartphones: any[];
  firebaseTabletModels: any[];
  // 🎧 웨어러블 추가
  firebaseWearableModels?: any[];
  selectedConfiguration?: any; // 웨어러블 선택된 구성
}

export const useGradePrices = (params: UseGradePricesParams) => {
  const [selectedGrade, setSelectedGrade] = useState<DeviceGrade | null>(null);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [allGradePrices, setAllGradePrices] = useState<{ A: number; B: number; C: number; D: number; E: number } | null>(null);

  // 🔥 모든 등급별 가격 조회 함수 (unified_prices Firebase 기반)
  // ⚠️ [버그 수정] params 객체 전체를 의존성으로 사용하면 무한 루프 발생
  // 개별 속성으로 분리하여 안정적인 의존성 관리
  const getAllGradePrices = useCallback(async (): Promise<{ A: number; B: number; C: number; D: number; E: number } | null> => {
    try {
      // 🔍 현재 선택된 기기 정보로 가격 조회
      let deviceType = '';
      let deviceModel = '';
      let resolvedStorage: string = '기본';
      let connectivity: string | undefined = undefined;
      let modelId: string = '';
      // 🔒 SSOT 가드용: 매칭된 카탈로그 모델 (storageOptions 검증에 사용)
      let catalogModel: any = null;

      if (params.category === '스마트폰' && params.selectedManufacturer === 'APPLE' && params.selectedIphoneModel) {
        // 🍎 스마트폰 - Apple (Firebase 기반)
        deviceType = '스마트폰';
        deviceModel = params.selectedIphoneModel;
        resolvedStorage = (params.selectedIphoneStorage || params.customIphoneStorage) || '기본';
        
        // 🔍 Firebase에서 실제 iPhone 모델 ID 찾기
        // 🔑 버그 수정: storageOptions 미포함 시에도 modelKo로 폴백 매칭
        const matchingIPhoneModel = params.firebaseAppleSmartphones.find(m =>
          m.modelKo === params.selectedIphoneModel &&
          m.storageOptions?.includes(resolvedStorage)
        ) || params.firebaseAppleSmartphones.find(m =>
          m.modelKo === params.selectedIphoneModel
        );

        if (matchingIPhoneModel) {
          modelId = matchingIPhoneModel.id;
          catalogModel = matchingIPhoneModel;
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 iPhone 가격 조회 준비: ${modelId}, ${resolvedStorage}`);
          }
        }
      } else if (params.category === '스마트폰' && params.selectedManufacturer === 'SAMSUNG' && params.selectedSamsungModel) {
        // 🤖 스마트폰 - Samsung (Firebase 기반)
        deviceType = '스마트폰';
        deviceModel = params.selectedSamsungModel;
        resolvedStorage = (params.selectedSamsungStorage || params.customSamsungStorage) || '기본';

        // 🔍 Firebase에서 실제 Samsung 모델 ID 찾기
        // 🔑 버그 수정: UI에서 selectedSamsungModel은 영문(modelEn)으로 설정되므로
        //    modelEn과 modelKo 모두 비교하여 매칭
        const matchingSamsungModel = params.firebaseSamsungSmartphones.find(m =>
          (m.modelEn === params.selectedSamsungModel || m.modelKo === params.selectedSamsungModel) &&
          m.storageOptions?.includes(resolvedStorage)
        ) || params.firebaseSamsungSmartphones.find(m =>
          m.modelEn === params.selectedSamsungModel || m.modelKo === params.selectedSamsungModel
        );

        if (matchingSamsungModel) {
          modelId = matchingSamsungModel.id;
          catalogModel = matchingSamsungModel;
        }
      } else if (params.category === '태블릿' && params.selectedManufacturer === 'SAMSUNG' && params.selectedSamsungTabletModel) {
        // 🤖 태블릿 - Samsung (Firebase 기반)
        deviceType = '태블릿';
        deviceModel = params.selectedSamsungTabletModel;
        resolvedStorage = (params.selectedSamsungTabletStorage || params.customSamsungTabletStorage) || '기본';
        connectivity = params.selectedSamsungTabletConnectivity;
        
        // Firebase에서 실제 Samsung 모델 ID 찾기
        const matchingSamsungModel = params.firebaseTabletModels.find(m =>
          m.brand === 'Samsung' && m.modelKo === params.selectedSamsungTabletModel
        );

        if (matchingSamsungModel) {
          modelId = matchingSamsungModel.id;
          catalogModel = matchingSamsungModel;
        }
      } else if (params.category === '태블릿' && params.selectedManufacturer === 'APPLE' && params.selectedModel && params.selectedSubModel) {
        // 🔥 태블릿 - Apple (Firebase 기반)
        deviceType = '태블릿';
        deviceModel = params.selectedModel.name;
        resolvedStorage = params.selectedSubModel.storage || '기본';
        connectivity = params.selectedSubModel.connectivity;
        
        // Firebase에서 실제 모델 ID 찾기
        const matchingFirebaseModel = params.firebaseTabletModels.find(m =>
          m.modelKo === params.selectedModel.name
        );

        if (matchingFirebaseModel) {
          modelId = matchingFirebaseModel.id;
          catalogModel = matchingFirebaseModel;
        }
      } else if (params.category === '웨어러블' && params.selectedModel) {
        // 🎧 웨어러블 - Apple (Firebase 기반)
        deviceType = '웨어러블';
        deviceModel = params.selectedModel.name;
        // 🎧 구성 선택이 있으면 구성명을 storage로 사용 (등급별 가격 조회)
        resolvedStorage = params.selectedConfiguration?.name || '기본';
        
        // 🔍 Firebase에서 실제 웨어러블 모델 ID 찾기
        // 1차: firebaseId가 있으면 사용 (변환된 모델)
        if (params.selectedModel.firebaseId) {
          modelId = params.selectedModel.firebaseId;
          if (process.env.NODE_ENV === 'development') {
            console.log(`🎧 웨어러블 가격 조회 (firebaseId): ${modelId}`);
          }
        } else if (params.firebaseWearableModels?.length) {
          // 2차: 모델명으로 Firebase에서 찾기
          const matchingWearable = params.firebaseWearableModels.find(m => 
            m.modelKo === params.selectedModel.name
          );
          
          if (matchingWearable) {
            modelId = matchingWearable.id;
            catalogModel = matchingWearable;
            if (process.env.NODE_ENV === 'development') {
              console.log(`🎧 웨어러블 가격 조회 (Firebase 매칭): ${modelId}`);
            }
          }
        }
      } else if (params.selectedModel) {
        deviceType = params.category || '';
        deviceModel = params.selectedModel.name;
      }

      // 🔒 SSOT 가드: 관리자 카탈로그에 없는(또는 비활성) 용량은 가격 조회 자체를 차단
      //    → unified_prices에 잔존하는 유령 가격 문서가 사용자에게 노출되는 것을 방지
      //    → 차단 시 기존 '가격 미설정(상담)' UX와 동일하게 null 반환
      if (catalogModel && resolvedStorage && !isStorageAllowedByCatalog(catalogModel, resolvedStorage)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔒 카탈로그 미등록 구성 차단: ${deviceModel} / ${resolvedStorage} (가격 미제공)`);
        }
        return null;
      }

      // 🔥 Firebase unified_prices에서 가격 조회 (성능 최적화)
      if (modelId && resolvedStorage) {
        try {
          const UnifiedPriceService = getUnifiedPriceService();

          // 🔥 브랜드별 가격 조회 (Apple + Samsung + LG 통합)
          // 🎧 웨어러블은 기본적으로 Apple (AirPods 등), selectedManufacturer가 없으면 Apple
          let targetBrand: 'Apple' | 'Samsung' | 'LG' = 'Apple';
          if (params.selectedManufacturer === 'SAMSUNG') {
            targetBrand = 'Samsung';
          } else if (params.selectedManufacturer === 'LG') {
            targetBrand = 'LG';
          } else if (params.selectedManufacturer === 'APPLE') {
            targetBrand = 'Apple';
          } else if (params.category === '웨어러블') {
            // 웨어러블은 제조사 선택이 없으므로 기본 Apple
            targetBrand = 'Apple';
          }

          // 🎧 웨어러블 카테고리 지원 추가
          const targetDeviceType = params.category === '스마트폰' ? 'smartphone' :
                                  params.category === '태블릿' ? 'tablet' :
                                  params.category === '웨어러블' ? 'wearable' : 'wearable';

          const allUnifiedPrices = await UnifiedPriceService.getPricesForManagement(
            targetBrand,
            targetDeviceType
          ) as any[]; // 🔧 타입 단언으로 TypeScript 오류 해결

          // 🔧 타입 가드 함수 정의
          const hasGradePrices = (obj: any): obj is { gradePrices: { A: number; B: number; C: number; D: number; E: number } } => {
            return obj && typeof obj.gradePrices === 'object' &&
                   typeof obj.gradePrices.A === 'number' &&
                   typeof obj.gradePrices.B === 'number' &&
                   typeof obj.gradePrices.C === 'number' &&
                   typeof obj.gradePrices.D === 'number';
          };

          // 🔑 버그 수정: unified_prices의 variant.storage는 소문자("256gb")로 저장되지만
          //    사용자 UI에서 선택된 resolvedStorage는 대문자("256GB")이므로
          //    비교 시 대소문자 무시하여 매칭
          const normalizeStorageForCompare = (s: string | undefined): string => {
            return (s || '').toLowerCase().replace(/[^a-z0-9가-힣]/g, '');
          };
          const normalizedResolvedStorage = normalizeStorageForCompare(resolvedStorage);

          // 1차 시도: 스마트폰/태블릿/웨어러블 구분 매칭
          let matchingPrice;

          // 🔥 modelId 정규화 함수 (웨어러블 호환)
          const normalizeModelId = (id: string | undefined) => id?.replace(/-+$/, '') || '';

          // 🔑 canonical modelId로 정규화 (device_catalog ID와 unified_prices modelId 형식 차이 해결)
          const canonicalModelId = canonicalizeModelId(modelId);

          if (targetDeviceType === 'smartphone') {
            // 🍎 스마트폰: connectivity 무시, modelId + storage만 매칭
            matchingPrice = allUnifiedPrices.find((price: any) => {
              const priceCanonicalModelId = canonicalizeModelId(price.modelId || '');
              const priceStorage = normalizeStorageForCompare(price.variant?.storage);
              return (price.modelId === modelId || priceCanonicalModelId === canonicalModelId) &&
                priceStorage === normalizedResolvedStorage;
            });
          } else if (targetDeviceType === 'wearable') {
            // 🎧 웨어러블: modelId + 구성(storage)으로 매칭
            matchingPrice = allUnifiedPrices.find((price: any) => {
              const priceModelId = normalizeModelId(price.modelId);
              const targetModelId = normalizeModelId(modelId);
              const priceStorage = normalizeStorageForCompare(price.variant?.storage);
              return priceModelId === targetModelId &&
                priceStorage === normalizedResolvedStorage;
            });
          } else {
            // 🖥️ 태블릿: connectivity 포함 매칭 (기존 로직)
            matchingPrice = allUnifiedPrices.find((price: any) => {
              const priceCanonicalModelId = canonicalizeModelId(price.modelId || '');
              const priceStorage = normalizeStorageForCompare(price.variant?.storage);
              return (price.modelId === modelId || priceCanonicalModelId === canonicalModelId) &&
                priceStorage === normalizedResolvedStorage &&
                (price.variant?.connectivity || 'Wi-Fi') === (connectivity || 'Wi-Fi');
            });
          }

          if (!matchingPrice) {
            // 2차 시도: 부분 매칭 (modelId 유사성)
            if (targetDeviceType === 'smartphone') {
              matchingPrice = allUnifiedPrices.find((price: any) => {
                const priceCanonicalModelId = canonicalizeModelId(price.modelId || '');
                const priceStorage = normalizeStorageForCompare(price.variant?.storage);
                return (price.modelId?.includes(modelId.split('-').slice(-2).join('-')) ||
                  priceCanonicalModelId?.includes(canonicalModelId.split('-').slice(-2).join('-'))) &&
                  priceStorage === normalizedResolvedStorage;
              });
            } else if (targetDeviceType === 'wearable') {
              // 🎧 웨어러블: 부분 매칭 (modelId + 구성)
              matchingPrice = allUnifiedPrices.find((price: any) => {
                const priceModelId = normalizeModelId(price.modelId);
                const priceStorage = normalizeStorageForCompare(price.variant?.storage);
                return priceModelId?.includes(modelId.split('-').slice(-2).join('-')) &&
                  priceStorage === normalizedResolvedStorage;
              });
            } else {
              // 태블릿: 기존 로직
              matchingPrice = allUnifiedPrices.find((price: any) => {
                const priceCanonicalModelId = canonicalizeModelId(price.modelId || '');
                const priceStorage = normalizeStorageForCompare(price.variant?.storage);
                return (price.modelId?.includes(modelId.split('-').slice(-2).join('-')) ||
                  priceCanonicalModelId?.includes(canonicalModelId.split('-').slice(-2).join('-'))) &&
                  priceStorage === normalizedResolvedStorage;
              });
            }
          }

          if (!matchingPrice) {
            // 3차 시도: 모델명으로 매칭
            const modelNameKey = params.selectedModel?.name?.toLowerCase().replace(/\s+/g, '');
            matchingPrice = allUnifiedPrices.find((price: any) => {
              const priceModelName = price.modelId?.toLowerCase().replace(/\s+/g, '');
              const priceStorage = normalizeStorageForCompare(price.variant?.storage);
              return priceModelName?.includes(modelNameKey) &&
                     priceStorage === normalizedResolvedStorage;
            });
          }

          // 🔧 타입 가드를 사용한 안전한 가격 접근
          if (matchingPrice && hasGradePrices(matchingPrice)) {
            const allGrades = matchingPrice.gradePrices;

            // 가격 유효성 검증
            const hasValidPrice = Object.values(allGrades).some(price => price > 0);
            if (hasValidPrice) {
              return allGrades;
            }
          }

        } catch (error) {
          console.error('unified_prices 가격 조회 실패:', error);
        }
      }

      // 🔄 레거시 방식 폴백 (기존 호환성)
      if (deviceType && deviceModel) {
        const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
        const prices: { A: number; B: number; C: number; D: number; E: number } = {
          A: 0, B: 0, C: 0, D: 0, E: 0
        };

        // 모든 등급의 가격을 조회 (레거시)
        for (const grade of grades) {
          const price = await firestorePriceApiService.lookupPrice(deviceType, deviceModel, resolvedStorage, grade);
          if (price && price > 0) {
            prices[grade] = price;
          }
        }

        return prices;
      }

      return null;
    } catch (error) {
      console.error('가격 조회 실패:', error);
      return null;
    }
  }, [
    // ⚠️ [버그 수정] 개별 속성으로 의존성 분리 - 무한 루프 방지
    params.category,
    params.selectedManufacturer,
    params.selectedIphoneModel,
    params.selectedIphoneStorage,
    params.customIphoneStorage,
    params.selectedSamsungModel,
    params.selectedSamsungStorage,
    params.customSamsungStorage,
    params.selectedSamsungTabletModel,
    params.selectedSamsungTabletStorage,
    params.customSamsungTabletStorage,
    params.selectedSamsungTabletConnectivity,
    params.selectedModel,
    params.selectedSubModel,
    params.firebaseAppleSmartphones,
    params.firebaseSamsungSmartphones,
    params.firebaseTabletModels,
    // 🎧 웨어러블 의존성 추가
    params.firebaseWearableModels,
    params.selectedConfiguration
  ]);

  // 등급별 가격 계산 함수 (Mock API 사용) - 기존 호환성 유지
  const calculateGradePrice = useCallback(async (grade: DeviceGrade): Promise<number> => {
    const allPrices = await getAllGradePrices();
    if (allPrices) {
      return allPrices[grade];
    }

    // API에서 가격을 찾을 수 없는 경우 기본값 사용
    const fallbackPrices = {
      A: 640000,
      B: 520000,
      C: 380000,
      D: 220000,
      E: 100000
    };
    return fallbackPrices[grade];
  }, [getAllGradePrices]);

  // 등급 선택 핸들러 (비동기)
  const handleGradeSelect = useCallback(async (grade: DeviceGrade) => {
    setSelectedGrade(grade);
    const price = await calculateGradePrice(grade);
    setEstimatedPrice(price);
    // 등급 선택 시 관리자 설정 가격(0/미설정 제외)로 동기화
    if (price && price > 0) {
      setAllGradePrices(prev => ({ ...(prev || { A: 0, B: 0, C: 0, D: 0, E: 0 }), [grade]: price }));
    }
  }, [calculateGradePrice]);

  // 모델/용량 선택 시 모든 등급 가격 미리 로드 (Apple/Samsung 모두 지원)
  // ⚠️ [버그 수정] getAllGradePrices를 의존성에서 제거 - 무한 루프 방지
  // getAllGradePrices는 이미 필요한 모든 params를 의존성으로 가지고 있으므로
  // 여기서는 실제 값이 변경될 때만 실행되도록 함
  useEffect(() => {
    const loadAllGradePrices = async () => {
      const isAppleReady = params.category === '스마트폰' && params.selectedManufacturer === 'APPLE' && params.selectedIphoneModel && params.selectedIphoneStorage;
      const isSamsungReady = params.category === '스마트폰' && params.selectedManufacturer === 'SAMSUNG' && params.selectedSamsungModel && params.selectedSamsungStorage;
      const isOtherReady = params.category !== '스마트폰' && params.category !== '태블릿' && params.category !== '웨어러블' && params.selectedModel;
      const isTabletSamsungReady = params.category === '태블릿' && params.selectedManufacturer === 'SAMSUNG' && params.selectedSamsungTabletModel && (params.selectedSamsungTabletStorage || params.customSamsungTabletStorage) && !!params.selectedSamsungTabletConnectivity;
      const isTabletAppleReady = params.category === '태블릿' && params.selectedManufacturer === 'APPLE' && params.selectedModel && params.selectedSubModel; // iPad는 세부모델 기반
      // 🎧 웨어러블 준비 상태 (모델 선택만으로 가격 조회 가능)
      const isWearableReady = params.category === '웨어러블' && params.selectedModel;
      
      if (isAppleReady || isSamsungReady || isOtherReady || isTabletSamsungReady || isTabletAppleReady || isWearableReady) {
        const prices = await getAllGradePrices();
        setAllGradePrices(prices);
      }
    };

    loadAllGradePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // ⚠️ [버그 수정] getAllGradePrices 제거 - 무한 루프 방지
    // getAllGradePrices 내부에서 이미 동일한 params를 참조하므로 중복 의존성 불필요
    params.category, 
    params.selectedManufacturer, 
    params.selectedIphoneModel, 
    params.selectedIphoneStorage,
    params.selectedSamsungModel, 
    params.selectedSamsungStorage, 
    params.selectedModel, 
    params.selectedSamsungTabletModel,
    params.selectedSamsungTabletStorage, 
    params.customSamsungTabletStorage, 
    params.selectedSubModel,
    params.selectedSamsungTabletConnectivity,
    // 🎧 웨어러블 구성 선택 시 가격 재조회
    params.selectedConfiguration
  ]);

  return {
    selectedGrade,
    estimatedPrice,
    allGradePrices,
    setSelectedGrade,
    setEstimatedPrice,
    setAllGradePrices,
    getAllGradePrices,
    calculateGradePrice,
    handleGradeSelect
  };
};
